import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import DashboardLayout from '../../components/DashboardLayout';
import { Skeleton } from '../../components/Skeleton';
import Icon from '../../components/Icon';
import { compact } from '../../lib/metrics';
import { formatDateTime } from '../../lib/time';
import { fmtRoas } from '../Growth';

const money = (n, lang) =>
  n == null ? '—' : '₺' + Number(n).toLocaleString(lang === 'en' ? 'en-US' : 'tr-TR', { maximumFractionDigits: 0 });

export default function AdminGrowth() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('publisher');

  useEffect(() => {
    api.adminCampaigns().then(setData).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <DashboardLayout title={t('adminGrowth.title')} heading={false}>
        <Skeleton className="sk-block" style={{ marginTop: 20 }} />
      </DashboardLayout>
    );
  }

  const T = data?.totals || {};
  const rows = data?.campaigns || [];

  return (
    <DashboardLayout title={t('adminGrowth.title')} heading={false}>
      <h1 className="page-title">{t('adminGrowth.title')}</h1>
      <p className="page-sub">{t('adminGrowth.sub')}</p>

      <div className="growth-summary card">
        <div><span>{t('adminGrowth.total_spend')}</span><strong>{money(T.spend, lang)}</strong></div>
        <div><span>{t('adminGrowth.total_clicks')}</span><strong>{compact(T.clicks || 0, lang)}</strong></div>
        <div><span>{t('adminGrowth.est_regs')}</span><strong>{compact(T.estRegistrations || 0, lang)}</strong></div>
        {T.roas != null ? (
          <>
            <div><span>{t('adminGrowth.revenue')}</span><strong>{money(T.revenue, lang)}</strong></div>
            <div><span>{t('adminGrowth.roas')}</span><strong className={T.roas >= 1 ? 'pos' : 'neg'}>{fmtRoas(T.roas, lang)}</strong></div>
          </>
        ) : (
          <div><span>{t('adminGrowth.cpr')}</span><strong>{money(T.costPerRegistration, lang)}</strong></div>
        )}
        <div><span>{t('adminGrowth.campaigns')}</span><strong>{T.activeCampaigns || 0}<span style={{ fontSize: '0.9rem', color: 'var(--text-3)', fontWeight: 400 }}> / {T.totalCampaigns || 0}</span></strong></div>
      </div>
      <p className="growth-disclaimer">{t('growth.estimate_note')}</p>

      {rows.length === 0 ? (
        <div className="card" style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 'var(--sp-4)' }}>{t('adminGrowth.empty')}</div>
      ) : (
        <>
          <div className="ag-tabs" style={{ marginTop: 'var(--sp-5)' }}>
            <button className={tab === 'publisher' ? 'active' : ''} onClick={() => setTab('publisher')}>{t('adminGrowth.by_publisher')}</button>
            <button className={tab === 'customer' ? 'active' : ''} onClick={() => setTab('customer')}>{t('adminGrowth.by_customer')}</button>
            <button className={tab === 'all' ? 'active' : ''} onClick={() => setTab('all')}>{t('adminGrowth.all_campaigns')}</button>
          </div>

          <div className="card" style={{ padding: 0, marginTop: 'var(--sp-3)', overflowX: 'auto' }}>
            {tab === 'publisher' && (
              <table className="ag-table">
                <thead><tr>
                  <th>{t('adminGrowth.publisher')}</th><th>{t('adminGrowth.spend')}</th>
                  <th>{t('adminGrowth.campaigns')}</th><th>{t('adminGrowth.customers')}</th>
                  <th>{t('adminGrowth.clicks')}</th><th>{t('adminGrowth.est_regs')}</th>
                  <th>{t('adminGrowth.revenue')}</th><th>{t('adminGrowth.roas')}</th><th>{t('adminGrowth.cpr')}</th>
                </tr></thead>
                <tbody>
                  {data.byPublisher.map((p, i) => (
                    <tr key={i}>
                      <td>{p.publisherName}</td>
                      <td>{money(p.spend, lang)}</td>
                      <td>{p.campaigns}</td>
                      <td>{p.customerCount}</td>
                      <td>{compact(p.clicks, lang)}</td>
                      <td>{compact(p.estRegistrations, lang)}</td>
                      <td>{p.revenue != null ? money(p.revenue, lang) : '—'}</td>
                      <td>{p.roas != null ? <span className={p.roas >= 1 ? 'pos' : 'neg'}>{fmtRoas(p.roas, lang)}</span> : '—'}</td>
                      <td>{money(p.costPerRegistration, lang)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {tab === 'customer' && (
              <table className="ag-table">
                <thead><tr>
                  <th>{t('adminGrowth.customer')}</th><th>{t('adminGrowth.spend')}</th>
                  <th>{t('adminGrowth.campaigns')}</th><th>{t('adminGrowth.clicks')}</th>
                  <th>{t('adminGrowth.est_regs')}</th>
                  <th>{t('adminGrowth.revenue')}</th><th>{t('adminGrowth.roas')}</th><th>{t('adminGrowth.cpr')}</th>
                </tr></thead>
                <tbody>
                  {data.byCustomer.map((c) => (
                    <tr key={c.customer.id}>
                      <td><Link to={`/admin/customers/${c.customer.id}`}>{c.customer.displayName || c.customer.email}</Link></td>
                      <td>{money(c.spend, lang)}</td>
                      <td>{c.campaigns}</td>
                      <td>{compact(c.clicks, lang)}</td>
                      <td>{compact(c.estRegistrations, lang)}</td>
                      <td>{c.revenue != null ? money(c.revenue, lang) : '—'}</td>
                      <td>{c.roas != null ? <span className={c.roas >= 1 ? 'pos' : 'neg'}>{fmtRoas(c.roas, lang)}</span> : '—'}</td>
                      <td>{money(c.costPerRegistration, lang)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {tab === 'all' && (
              <table className="ag-table">
                <thead><tr>
                  <th>{t('adminGrowth.customer')}</th><th>{t('adminGrowth.server')}</th>
                  <th>{t('adminGrowth.publisher')}</th><th>{t('adminGrowth.channel')}</th>
                  <th>{t('adminGrowth.spend')}</th><th>{t('adminGrowth.period')}</th>
                  <th>{t('adminGrowth.clicks')}</th><th>{t('adminGrowth.est_regs')}</th>
                  <th>{t('adminGrowth.roas')}</th><th>{t('adminGrowth.cpr')}</th>
                </tr></thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.id}>
                      <td>{c.customer ? <Link to={`/admin/customers/${c.customer.id}`}>{c.customer.displayName || c.customer.email}</Link> : '—'}</td>
                      <td>{c.serverLabel || '—'}</td>
                      <td>{c.publisherName}</td>
                      <td>{t(`growth.channel_${c.channel}`, { defaultValue: c.channel })}</td>
                      <td>{money(c.spendAmount, lang)}</td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                        {formatDateTime(c.startDate, lang).slice(0, 10)}
                        {c.status === 'ended' ? ` – ${c.endDate ? formatDateTime(c.endDate, lang).slice(0, 10) : ''}` : ` – ${t('growth.ongoing')}`}
                      </td>
                      <td>{compact(c.stats.clicks, lang)}</td>
                      <td>{compact(c.stats.estRegistrations, lang)} <span className={`growth-conf ${c.stats.confidence}`}>{t(`growth.conf_${c.stats.confidence}`)}</span></td>
                      <td>{c.stats.roas != null ? <span className={c.stats.roas >= 1 ? 'pos' : 'neg'}>{fmtRoas(c.stats.roas, lang)}</span> : '—'}</td>
                      <td>{money(c.stats.costPerRegistration, lang)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </DashboardLayout>
  );
}
